import * as React from 'react'
import {nls} from '../../nls'
import {AttachmentInfos, AttachmentInfo} from '../../../interfaces/models'
import {Feature} from '../../../../core/models'

export interface Props {
  readonly?: boolean
  attachments: AttachmentInfos
  adds: AttachmentInfos
  updates: AttachmentInfos
  deletes: AttachmentInfos
  maxNumber?: number
  acceptTypes?: string
  multiple?: boolean
  previewIfPictures?: boolean
  addButtonContent?: JSX.Element
  removeButtonContent?: JSX.Element
  onAttachmentsChange: (changes: {adds: AttachmentInfos; updates: AttachmentInfos; deletes: AttachmentInfos}) => void
  feature: Feature
  uploadToArchive: boolean
}

interface State {
  errorMessage?: string
  attachments: AttachmentInfos
  loading: boolean
}

export class AttachmentsViewer extends React.Component<Props, State> {
  protected fileInput: HTMLInputElement
  constructor(props: Props) {
    super(props)
    this.getAttachmentsFromProps = this.getAttachmentsFromProps.bind(this)
    this.state = {
      attachments: this.getAttachmentsFromProps(props),
      loading: false
    }
    this.onAddAttachmentClick = this.onAddAttachmentClick.bind(this)
    this.onRemoveAttachmentClick = this.onRemoveAttachmentClick.bind(this)
    this.onAddAttachments = this.onAddAttachments.bind(this)
    this.applyNewAttachments = this.applyNewAttachments.bind(this)
  }

  componentDidMount() {
    if (this.props.previewIfPictures) {
      this.setState({...this.state, loading: true}, () => {
        const promises: Promise<AttachmentInfo>[] = []
        const atts = this.getAttachmentsFromProps(this.props)
        for (const name in atts) {
          promises.push(this.loadAttachment(atts[name]))
        }
        Promise.all(promises)
          .then(ats => {
            ats.forEach(a => [(atts[a.name] = a)])
            this.setState({...this.state, errorMessage: '', loading: false, attachments: {...atts}})
          })
          .catch(e => {
            console.error(e)
            this.setState({...this.state, errorMessage: e.message, loading: false})
          })
      })
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.feature !== this.props.feature) {
      this.setState({...this.state, attachments: this.getAttachmentsFromProps(this.props)})
    }
  }

  filterAttachments(atts: AttachmentInfos): AttachmentInfos {
    return atts
  }

  protected getAttachmentsFromProps(props: Props): AttachmentInfos {
    const attachments: AttachmentInfos = {...props.attachments, ...props.adds, ...props.updates}
    for (const key in props.deletes) {
      delete attachments[key]
    }
    return this.filterAttachments(attachments)
  }

  protected onAddAttachmentClick(): void {
    if (!this.props.readonly) {
      this.fileInput.click()
    }
  }

  protected loadAttachment = (attachmentInfo: AttachmentInfo): Promise<AttachmentInfo> => {
    if (attachmentInfo.file && !attachmentInfo.src) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        ;(reader as any).onload = (e: Event & {target: {result: string}}) => {
          attachmentInfo.src = e.target.result
          attachmentInfo.size = attachmentInfo.file!.size
          resolve(attachmentInfo)
        }
        reader.readAsDataURL(attachmentInfo.file!)
      })
    }
    return Promise.resolve(attachmentInfo)
  }

  protected onRemoveAttachmentClick(key: string): void {
    if (!this.props.readonly) {
      const adds = {...this.props.adds}
      const updates = {...this.props.updates}
      const deletes = {...this.props.deletes}

      if (updates[key] || this.props.attachments[key]) {
        deletes[key] = this.state.attachments[key]
      }
      delete adds[key]
      delete updates[key]
      this.fileInput.value = ''
      this.props.onAttachmentsChange({
        adds,
        updates,
        deletes
      })
    }
  }

  protected applyNewAttachments(news: AttachmentInfo[]) {
    const updated: AttachmentInfos = {...this.props.updates}
    const adds: AttachmentInfos = {...this.props.adds}
    const deletes: AttachmentInfos = {...this.props.deletes}

    news.forEach(newAttachmentInfo => {
      delete deletes[newAttachmentInfo.name]
      if (this.props.attachments[newAttachmentInfo.name]) {
        updated[newAttachmentInfo.name] = newAttachmentInfo
      } else {
        adds[newAttachmentInfo.name] = newAttachmentInfo
      }
    })
    this.props.onAttachmentsChange({
      adds: adds,
      updates: updated,
      deletes: deletes
    })
  }

  protected getNewFileName(file: File): string {
    return file.name
  }

  protected getNewAttachments(files: FileList): AttachmentInfo[] {
    const newAttachmentInfos: AttachmentInfo[] = []
    for (let i = 0; i < files.length; i++) {
      const newAttachmentInfo: AttachmentInfo = {
        file: files[i],
        name: this.getNewFileName(files[i]),
        type: files[i].type,
        size: files[i].size
      }
      newAttachmentInfos.push(newAttachmentInfo)
    }
    return newAttachmentInfos
  }

  protected onAddAttachments(event: React.ChangeEvent<HTMLInputElement>): void {
    if (event.target.files && event.target.files.length > 0) {
      const newAttachmentInfos: AttachmentInfo[] = this.getNewAttachments(event.target.files)
      this.applyNewAttachments(newAttachmentInfos)
      this.fileInput.value = ''
    }
  }

  getRemoveAttachmentButton(key: string): JSX.Element {
    return this.props.readonly ? (
      <></>
    ) : (
      <div
        key={key}
        onClick={() => this.onRemoveAttachmentClick(key)}
        style={{maxWidth: '32px', cursor: 'pointer'}}
        title={nls.general.remove}
      >
        {this.props.removeButtonContent ? (
          this.props.removeButtonContent
        ) : (
          <img src="resources/icons/remove-file.svg" style={{width: '25px', height: '25px'}} alt="" />
        )}
      </div>
    )
  }

  protected getAttachmentsList(stateAttachments: AttachmentInfos): JSX.Element {
    const attachments: Array<JSX.Element> = []
    for (const key in stateAttachments) {
      attachments.push(
        <div
          key={key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: '#f1f1f1',
            padding: '4px',
            alignItems: 'center',
            margin: '2px'
          }}
        >
          <div style={{flex: '1 auto'}}>
            {stateAttachments[key].src ? (
              <a target="_blank" href={stateAttachments[key].src} rel="noreferrer">
                {key}
              </a>
            ) : (
              <span>{key}</span>
            )}
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{marginLeft: '10px'}}>
              <span>{` ${((stateAttachments[key].size || 0) / 1000000).toFixed(2)} MB`}</span>
            </div>

            {this.getRemoveAttachmentButton(key)}
          </div>
        </div>
      )
    }

    return <React.Fragment>{attachments}</React.Fragment>
  }

  protected getAttachmentsPreview(stateAttachments: AttachmentInfos): JSX.Element {
    const attachments: Array<JSX.Element> = []
    for (const key in stateAttachments) {
      const fileString = stateAttachments[key].src
      attachments.push(
        <div
          key={key}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: '#f6f6f6',
            padding: '4px',
            alignItems: 'center',
            margin: '2px'
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '250px',
              height: '200px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden'
            }}
          >
            {this.props.readonly ? null : (
              <div
                onClick={() => this.onRemoveAttachmentClick(key)}
                style={{maxWidth: '32px', cursor: 'pointer', position: 'absolute', top: '0', right: '0'}}
                title={nls.general.remove}
              >
                {this.props.removeButtonContent ? (
                  this.props.removeButtonContent
                ) : (
                  <img src="resources/icons/minus.svg" style={{width: '25px', height: '25px'}} alt="" />
                )}
              </div>
            )}

            <div
              style={{
                textAlign: 'center',
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                border: '1px solid gray',
                backgroundColor: 'white',
                padding: '3px'
              }}
            >
              {key}
            </div>
            <img src={fileString ? fileString : 'resources/icons/loading.gif'} style={{height: '200px'}} alt="" />
          </div>
        </div>
      )
    }

    return <React.Fragment>{attachments}</React.Fragment>
  }

  areAllPictures(): boolean {
    let allPictures = true
    for (const name in this.state.attachments) {
      if (!(this.state.attachments[name].type.indexOf('image/') == 0)) {
        allPictures = false
        break
      }
    }
    return allPictures
  }

  protected getAddAttachmentBtnText(): string {
    return `${nls.general.add} ${nls.general.file}`
  }

  protected getAddAttachmentBtnStyle(): React.CSSProperties {
    return {cursor: 'pointer', display: 'flex'}
  }

  protected getAddAttachmentButton(stateAttachments: AttachmentInfos): JSX.Element {
    const title = this.getAddAttachmentBtnText()
    return Object.keys(stateAttachments).length >= (this.props.maxNumber || Infinity) || this.props.readonly ? (
      <div></div>
    ) : (
      <div onClick={this.onAddAttachmentClick} style={this.getAddAttachmentBtnStyle()} title={title}>
        <img src="resources/icons/add-file.svg" style={{width: '20px', height: '20px'}} alt="" />
        <div>{title}</div>
      </div>
    )
  }

  protected getRealInput(): JSX.Element {
    return (
      <input
        ref={ref => (this.fileInput = ref as HTMLInputElement)}
        type="file"
        multiple={this.props.multiple ? true : false}
        accept={this.props.acceptTypes}
        onChange={this.onAddAttachments}
        style={{display: 'none', visibility: 'hidden'}}
      />
    )
  }

  render() {
    return (
      <div style={{width: '100%'}}>
        {this.getRealInput()}
        {!!this.props.previewIfPictures && this.areAllPictures()
          ? this.getAttachmentsPreview(this.state.attachments)
          : this.getAttachmentsList(this.state.attachments)}
        {this.getAddAttachmentButton(this.state.attachments)}
      </div>
    )
  }
}
